import { Button } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import DisplaySection from './DisplaySection';
import SvgCompass from './SvgCompass';
import Location from '../Location';
import { useDrawer } from '../context/DrawerContext';

// Define a custom theme for consistent styling, especially for typography
const theme = createTheme({
  typography: {
    fontFamily: 'Inter, sans-serif', // Using Inter font as requested
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // Apply rounded corners to the card
        //   boxShadow: '0 4px 20px rgba(0,0,0,0.1)', // Add a subtle shadow
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure text is legible and has good contrast
          color: '#333',
        },
      },
    },
  },
});

/**
 * Converts a temperature from Celsius to Fahrenheit.
 * @param {number} celsius - The temperature in Celsius.
 * @returns {number} The temperature in Fahrenheit.
 */
const convertCelsiusToFahrenheit = (celsius) => {
  return (celsius * 9/5) + 32;
};

/**
 * WeatherCard component displays weather information including an icon,
 * condition name, and temperature in Fahrenheit.
 * @param {object} props - Component props.
 * @param {object} props.weatherData - The weather data object.
 * @param {string} props.weatherData.condition - The weather condition name (e.g., "Cloudy").
 * @param {string} props.weatherData.icon - The emoji icon representing the weather.
 * @param {object} props.weatherData.temperature - The temperature object.
 * @param {number} props.weatherData.temperature.value - The temperature value.
 * @param {string} props.weatherData.temperature.unit - The unit of temperature (e.g., "celsius").
 */
const WeatherCard = ({ location, weatherData }) => {
  const {openDrawer} = useDrawer();
  
    if (!weatherData) {
        return null; // Return null if no weather data is provided
    }

  const { condition, icon, temperature, windSpeed, gustSpeed, windDirection } = weatherData;

  const getWindDirection = (angle) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((angle %= 360) < 0 ? angle + 360 : angle) / 45) % 8;
    return directions[index];
  };

  const getCelsiusToFahrenheit = (celsius) => {
    return (celsius * 9 / 5) + 32;
  };

  const kmphToMph = (kmph) => kmph ? kmph * 0.621371 : null;

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        padding: '0rem',
        maxWidth: '300px',
        width: '100%',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Button 
        variant='contained' 
        sx={{marginBottom: 1}}
        onClick={() => openDrawer('forecast', {location}) }
        >
        Forecast
        </Button>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <DisplaySection
          color="gray"
          label="Condition"
          value={condition}
          graphic={<span>{icon}</span>}
        />
        <DisplaySection
          color="red"
          label="Temperature"
          value={Math.round(getCelsiusToFahrenheit(temperature.value))}
          unit="°F"
        />
        <DisplaySection
          color="blue"
          label="Wind Speed"
          value={kmphToMph(windSpeed?.value)?.toFixed(0)}
          unit="mph"
        />
        <DisplaySection
          color="purple"
          label="Wind Gust"
          value={kmphToMph(gustSpeed?.value)?.toFixed(0)}
          unit="mph"
        />
        <DisplaySection
          color="green"
          label="Wind Direction"
          value={getWindDirection(windDirection)}
          unit={`(${windDirection?.toFixed(0)}°)`}
          graphic={<SvgCompass angle={windDirection} />}
        />
      </div>
    </div>
  );


};

export default WeatherCard;